import { useEffect, useState } from "react";
import api from "../lib/api";
import { money, fmtDate, financialYear, PAYMENT_METHODS } from "../lib/format";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import { PiggyBank } from "lucide-react";

export default function DrawingsPage() {
  const [fy] = useState(financialYear());
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/reports/drawings", { params: { date_from: fy.from, date_to: fy.to } }).then((r) => setData(r.data));
  }, [fy]);

  const monthly = (data?.monthly || []).map((m) => ({ ...m, label: m.month.slice(5) }));

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <div className="w-11 h-11 rounded-xl bg-primary/10 text-primary grid place-items-center"><PiggyBank className="w-5 h-5" /></div>
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Drawings</h1>
          <p className="text-muted-foreground text-sm">Money taken from the business for personal use · <span className="font-mono">{fy.label}</span></p>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-6">
        <div className="rounded-2xl border border-border bg-card p-5" data-testid="drawings-total">
          <div className="text-xs uppercase tracking-wider font-mono text-muted-foreground">Total drawings</div>
          <div className="num text-2xl font-bold mt-2 text-rose-600">{money(data?.total)}</div>
        </div>
        <div className="rounded-2xl border border-border bg-card p-5">
          <div className="text-xs uppercase tracking-wider font-mono text-muted-foreground">Withdrawals</div>
          <div className="num text-2xl font-bold mt-2">{data?.count ?? 0}</div>
        </div>
        <div className="rounded-2xl border border-border bg-secondary/40 p-5 col-span-2 sm:col-span-1 flex items-center">
          <p className="text-xs text-muted-foreground">Drawings aren't a business expense — they're excluded from your P&amp;L, GST and tax. Tag an expense "Personal / drawings" to record one.</p>
        </div>
      </div>

      {monthly.length > 0 && (
        <div className="rounded-2xl border border-border bg-card p-5 mb-6" data-testid="drawings-chart">
          <h3 className="font-heading font-semibold">Monthly drawings</h3>
          <p className="text-xs text-muted-foreground mb-4">What you've taken each month</p>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={monthly}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2DDD3" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 11, fontFamily: "JetBrains Mono" }} stroke="#94A3B8" />
              <YAxis tick={{ fontSize: 11, fontFamily: "JetBrains Mono" }} stroke="#94A3B8" tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
              <Tooltip formatter={(v) => money(v)} contentStyle={{ borderRadius: 12, border: "1px solid #E2DDD3" }} />
              <Bar dataKey="amount" fill="#E11D48" radius={[6, 6, 0, 0]} maxBarSize={48} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      <div className="rounded-2xl border border-border bg-card overflow-hidden">
        <div className="px-4 py-3 bg-secondary/50 font-heading font-semibold text-sm">Recent drawings</div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-secondary/30 hover:bg-secondary/30">
                <TableHead>Date</TableHead>
                <TableHead>Description</TableHead>
                <TableHead className="hidden md:table-cell">Category</TableHead>
                <TableHead className="hidden sm:table-cell">Paid by</TableHead>
                <TableHead className="text-right">Amount</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {!data ? (
                <TableRow><TableCell colSpan={5} className="text-center py-10 text-muted-foreground">Loading…</TableCell></TableRow>
              ) : data.items.length === 0 ? (
                <TableRow><TableCell colSpan={5} className="text-center py-12 text-muted-foreground">No drawings recorded yet.</TableCell></TableRow>
              ) : data.items.map((t) => (
                <TableRow key={t.id} className="row-hover" data-testid={`drawing-row-${t.id}`}>
                  <TableCell className="num text-xs whitespace-nowrap">{fmtDate(t.date)}</TableCell>
                  <TableCell className="font-medium">{t.description}</TableCell>
                  <TableCell className="hidden md:table-cell text-sm text-muted-foreground">{t.category || "—"}</TableCell>
                  <TableCell className="hidden sm:table-cell text-sm text-muted-foreground">{PAYMENT_METHODS[t.payment_method] || "—"}</TableCell>
                  <TableCell className="num text-right font-semibold text-rose-600">{money(t.amount)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
